import { DOWN_ARROW, ENTER, LEFT_ARROW, RIGHT_ARROW, UP_ARROW } from '@angular/cdk/keycodes';
import { isDefined } from '../utils';
import { IAIAssistancePrompt } from './fi-ai-assistance';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';

export interface IAiAssistanceRequestContainerArgs {
    promptSamples: IAIAssistancePrompt[];
    promptSamplesTitle: string;
    inputPlaceholder: string;
    sendRequestFn: TSendRequestFn;
    clearRequestTooltipText: string;
    sendRequestTooltipText: string;
    requestMessage?: string;
}

interface IAIWrapperElBuilderArgs {
    inputPlaceholder: string;
    sendRequestFn: TSendRequestFn;
    sendRequestTooltipText: string;
    clearRequestTooltipText: string;
    requestMessage?: string;
}

interface IAIRequestActionsWrapperElArgs {
    sendRequestFn: TSendRequestFn;
    initialRequestMessage: string;
    clearRequestTooltipText: string;
    sendRequestTooltipText: string;
}

export type TSendRequestFn = (request: string) => void;

export const FI_AI_ASSISTANCE_EDITOR_ID: string = 'fi-ai-assistance-editor';
export const FI_AI_ASSISTANCE_REQUEST_INPUT_ID: string = 'fi-ai-request-input';
export const FI_AI_ASSISTANCE_PROMPT_SAMPLES_CONTAINER_ID: string = 'fi-ai-assistance-prompt-samples-container';
export const FI_AI_ASSISTANCE_REQUEST_ACTIONS_WRAPPER_ID: string = 'fi-ai-assistance-request-actions-wrapper';
export const FI_AI_ASSISTANCE_SEND_REQUEST_BUTTON_ID: string = 'fi-ai-assistance-send-request-button';
export const FI_AI_ASSISTANCE_CLEAR_REQUEST_BUTTON_ID: string = 'fi-ai-assistance-clear-request-button';

// warning: these class names are used in @mixin fi-text-item-ai-assistance
// after changing them here don't forget to change related in fi-text-item-ai-assistance.scss
const AI_ASSISTANCE_EDITOR_CLASS_NAME: string = 'fi-ai-assistance-editor';
const REQUEST_INPUT_CLASS_NAME: string = 'request-input';
const REQUEST_ACTIONS_WRAPPER_CLASS_NAME: string = 'request-actions-wrapper';
const SEND_AI_REQUEST_BUTTON_CLASS_NAME: string = 'send-ai-request-button';
const CLEAR_AI_REQUEST_BUTTON_CLASS_NAME: string = 'clear-ai-request-button';
const CLEAR_AI_REQUEST_VISIBLE_CLASS_NAME: string = 'fi-ai-assistance-clear-ai-request--visible';
const CLEAR_AI_REQUEST_BUTTON_ICON_CLASS_NAME: string = 'clear-ai-request-icon';
const REQUEST_ACTION_TOOLTIP_CLASS_NAME: string = 'request-action-tooltip';
const REQUEST_ACTION_TOOLTIP_VISIBLE_CLASS_NAME: string = 'request-action-tooltip__visible';
const SEND_AI_REQUEST_BUTTON_ICON_CLASS_NAME: string = 'send-ai-request-icon';
const AI_REQUEST_WRAPPER_CLASS_NAME: string = 'ai-request-wrapper';
const AI_REQUEST_MAIN_ICON_CLASS_NAME: string = 'panel-main-icon';
const PROMPT_SAMPLE_CONTAINER_VISIBLE_CLASS_NAME: string = 'fi-ai-assistance-prompt-samples-container--visible';
const PROMPT_SAMPLE_LABEL_CLASS_NAME: string = 'ai-prompt-sample-button-label';
const PROMPT_SAMPLE_BUTTON_CLASS_NAME: string = 'ai-prompt-sample-button';
const PROMPT_SAMPLE_BUTTON_ICON_CLASS_NAME: string = 'ai-prompt-samples-button-icon';
const PROMPT_SAMPLES_WRAPPER: string = 'ai-prompt-samples-wrapper';
const PROMPT_SAMPLES_TITTLE: string = 'prompt-samples-title';
//

/* ai request editor layout
<div [id]="FI_AI_ASSISTANCE_EDITOR_ID" [class]="AI_ASSISTANCE_EDITOR_CLASS_NAME">
    <div [class]="AI_REQUEST_WRAPPER_CLASS_NAME">
        <i [class]="AI_REQUEST_MAIN_ICON_CLASS_NAME"></i>
        <div contenteditable="true" [id]="FI_AI_ASSISTANCE_REQUEST_INPUT_ID" [class]="REQUEST_INPUT_CLASS_NAME"></div> -request input
        <div [id]="FI_AI_ASSISTANCE_REQUEST_ACTIONS_WRAPPER_ID" [class]="REQUEST_ACTIONS_WRAPPER_CLASS_NAME">
            <button [id]="FI_AI_ASSISTANCE_CLEAR_REQUEST_BUTTON_ID" [class]="CLEAR_AI_REQUEST_BUTTON_CLASS_NAME"> - clear request button
                <div [class]="REQUEST_ACTION_TOOLTIP_CLASS_NAME"></div>
                <i [class]="CLEAR_AI_REQUEST_BUTTON_ICON_CLASS_NAME"></i>
            </button>
            <button [id]="FI_AI_ASSISTANCE_SEND_REQUEST_BUTTON_ID" [class]="SEND_AI_REQUEST_BUTTON_CLASS_NAME"> - send request button
                <div [class]="REQUEST_ACTION_TOOLTIP_CLASS_NAME"></div>
                <i [class]="SEND_AI_REQUEST_BUTTON_ICON_CLASS_NAME"></i>
            </button>
        </div>
     </div>
    <div [id]="FI_AI_ASSISTANCE_PROMPT_SAMPLES_CONTAINER_ID" [class]="PROMPT_SAMPLES_WRAPPER" >
        <button [class]="PROMPT_SAMPLE_BUTTON_CLASS_NAME" >
            <i [class]="PROMPT_SAMPLE_BUTTON_ICON_CLASS_NAME"></i>
            <span [class]="PROMPT_SAMPLE_LABEL_CLASS_NAME"></span> - recent request message
        </button>
     </div>
</div>
*/

export const getAiRequestEditorElement = (args: IAiAssistanceRequestContainerArgs): HTMLDivElement => {
    const mainWrapper: HTMLDivElement = document.createElement('div');
    mainWrapper.setAttribute('contentEditable', 'false');
    mainWrapper.classList.add(AI_ASSISTANCE_EDITOR_CLASS_NAME);
    mainWrapper.id = FI_AI_ASSISTANCE_EDITOR_ID;
    mainWrapper.appendChild(
        getAIRequestWrapperElement({
            inputPlaceholder: args.inputPlaceholder,
            sendRequestFn: args.sendRequestFn,
            requestMessage: args.requestMessage,
            sendRequestTooltipText: args.sendRequestTooltipText,
            clearRequestTooltipText: args.clearRequestTooltipText,
        }),
    );
    mainWrapper.appendChild(getAIPromptSamplesContainerElement(args.promptSamples, args.promptSamplesTitle));
    return mainWrapper;
};

export const getExistingAiRequestEditorElement = (): HTMLElement => {
    return document.getElementById(FI_AI_ASSISTANCE_EDITOR_ID);
};

export const getExistingRequestInputEl = (): HTMLElement => {
    return document.getElementById(FI_AI_ASSISTANCE_REQUEST_INPUT_ID);
};

const getExistingClearRequestButtonEl = (): HTMLElement => {
    return document.getElementById(FI_AI_ASSISTANCE_CLEAR_REQUEST_BUTTON_ID);
};

const getEditorIconEl = (classList: string[]): HTMLElement => {
    const aiIcon: HTMLElement = document.createElement('i');
    aiIcon.classList.add(...classList);
    return aiIcon;
};

const getRequestInputEl = (
    requestMessage: string,
    inputPlaceholder: string,
    sendRequestFn: TSendRequestFn,
): HTMLDivElement => {
    const requestInputEl: HTMLDivElement = document.createElement('div');
    requestInputEl.setAttribute('contentEditable', 'true');
    requestInputEl.classList.add(REQUEST_INPUT_CLASS_NAME);
    requestInputEl.id = FI_AI_ASSISTANCE_REQUEST_INPUT_ID;
    requestInputEl.setAttribute('placeholder', inputPlaceholder);

    const handleEnterKeyDown = (e: KeyboardEvent) => {
        if (e.keyCode === ENTER && !e.shiftKey) {
            e.preventDefault();
            if (!!getExistingRequestInputEl().innerText?.length) sendRequestFn(requestInputEl.innerText);
            return;
        }
    };

    const handleFocusedElementChange = (e: KeyboardEvent) => {
        if ([UP_ARROW, DOWN_ARROW, LEFT_ARROW, RIGHT_ARROW].includes(e.keyCode)) {
            setTimeout(() => {
                const selection: Selection = window.getSelection();
                if (!!window.getSelection().rangeCount) {
                    const focusedNode: Node = selection.getRangeAt(0).startContainer;
                    const isChild: boolean = getExistingAiRequestEditorElement().contains(focusedNode);
                    if (isChild) return;

                    requestInputEl.setAttribute('focus_to_start', `${[UP_ARROW, LEFT_ARROW].includes(e.keyCode)}`);
                    requestInputEl.focus();
                }
            });
        }
    };

    const handleInputChange = (e?: Event) => {
        setTimeout(() => {
            handleAiRequestInputChange();
        });
    };

    requestInputEl.addEventListener('input', handleInputChange);
    requestInputEl.addEventListener('paste', handleInputChange);

    requestInputEl.addEventListener('keydown', (e: KeyboardEvent) => {
        handleEnterKeyDown(e);
        handleFocusedElementChange(e);
    });

    if (isDefined(requestMessage)) {
        requestInputEl.innerText = requestMessage;
        handleInputChange();
    }
    requestInputEl.addEventListener('focus', () => {
        let range: Range = document.createRange();
        let selection: Selection = window.getSelection();
        range.selectNodeContents(requestInputEl);
        const isToStartFocus: string = requestInputEl.getAttribute('focus_to_start');
        range.collapse(isToStartFocus === 'true');
        selection.removeAllRanges();
        selection.addRange(range);
    });
    return requestInputEl;
};

export const handleAiRequestInputChange = () => {
    const requestInputEl: HTMLElement = getExistingRequestInputEl();
    setPromptSampleContainerVisibility(!requestInputEl.innerText?.trim().length);
    filterPromptSamples(requestInputEl.innerText);
    updateSendRequestButtonElDisableStatus();
    setClearRequestButtonVisibility();
};

export const filterPromptSamples = (requestValue: string) => {
    const promptSampleButtons = Array.from(document.getElementsByClassName(PROMPT_SAMPLE_BUTTON_CLASS_NAME));
    if (!promptSampleButtons.length) {
        setPromptSampleContainerVisibility(false);
        return;
    }

    if (!requestValue?.trim().length) {
        promptSampleButtons.forEach((buttonEl: HTMLElement) => (buttonEl.style.display = 'flex'));
        setPromptSampleContainerVisibility(true);
        return;
    }

    let isSampleContainerVisible: boolean = false;
    promptSampleButtons.forEach((buttonEl: HTMLElement) => {
        const promptSampleValue: string = (
            buttonEl.querySelector('.' + PROMPT_SAMPLE_LABEL_CLASS_NAME) as HTMLSpanElement
        ).innerText
            .trim()
            .toLowerCase()
            .replace(/\u00A0/g, ' ');
        if (promptSampleValue.includes(requestValue.toLowerCase().replace(/\u00A0/g, ' '))) {
            isSampleContainerVisible = true;
            buttonEl.style.display = 'flex';
        } else {
            buttonEl.style.display = 'none';
        }
    });
    setPromptSampleContainerVisibility(isSampleContainerVisible);
};

const updateSendRequestButtonElDisableStatus = () => {
    const buttonEl: HTMLButtonElement = document.getElementById(
        FI_AI_ASSISTANCE_SEND_REQUEST_BUTTON_ID,
    ) as HTMLButtonElement;
    const inputEl: HTMLElement = document.getElementById(FI_AI_ASSISTANCE_REQUEST_INPUT_ID);

    if (isNullOrUndefined(inputEl) || isNullOrUndefined(buttonEl)) return;

    buttonEl.disabled = !inputEl.innerText?.trim().length;
};

const getAiRequestActionsWrapperEl = (args: IAIRequestActionsWrapperElArgs) => {
    const aiRequestActionsWrapperEl: HTMLDivElement = document.createElement('div');
    aiRequestActionsWrapperEl.id = FI_AI_ASSISTANCE_REQUEST_ACTIONS_WRAPPER_ID;
    aiRequestActionsWrapperEl.classList.add(REQUEST_ACTIONS_WRAPPER_CLASS_NAME);
    aiRequestActionsWrapperEl.appendChild(getClearRequestButtonEl(args.clearRequestTooltipText));
    aiRequestActionsWrapperEl.appendChild(
        getSendRequestButtonEl(args.sendRequestFn, args.initialRequestMessage, args.sendRequestTooltipText),
    );
    return aiRequestActionsWrapperEl;
};

const getClearRequestButtonEl = (tooltipText: string): HTMLButtonElement => {
    const clearButtonEl: HTMLButtonElement = document.createElement('button');
    clearButtonEl.id = FI_AI_ASSISTANCE_CLEAR_REQUEST_BUTTON_ID;
    clearButtonEl.classList.add(CLEAR_AI_REQUEST_BUTTON_CLASS_NAME);
    clearButtonEl.addEventListener('click', () => {
        const requestInputEl: HTMLElement = getExistingRequestInputEl();
        requestInputEl.innerText = '';
        handleAiRequestInputChange();
        clearButtonEl.classList.remove(CLEAR_AI_REQUEST_VISIBLE_CLASS_NAME);
    });
    initActionTooltip(clearButtonEl, tooltipText);
    clearButtonEl.appendChild(getEditorIconEl([CLEAR_AI_REQUEST_BUTTON_ICON_CLASS_NAME, 'far', 'fa-xmark']));
    return clearButtonEl;
};

const getSendRequestButtonEl = (
    sendRequestFn: TSendRequestFn,
    initialRequestMessage: string,
    tooltipText: string,
): HTMLButtonElement => {
    const sendRequestButtonEl: HTMLButtonElement = document.createElement('button');
    sendRequestButtonEl.id = FI_AI_ASSISTANCE_SEND_REQUEST_BUTTON_ID;
    sendRequestButtonEl.classList.add(SEND_AI_REQUEST_BUTTON_CLASS_NAME);
    sendRequestButtonEl.setAttribute('disabled', `${!initialRequestMessage?.length}`);
    sendRequestButtonEl.addEventListener('click', () => {
        const requestInputEl: HTMLElement = getExistingRequestInputEl();
        if (!!requestInputEl.innerText?.length) sendRequestFn(requestInputEl.innerText);
    });
    initActionTooltip(sendRequestButtonEl, tooltipText);
    sendRequestButtonEl.appendChild(
        getEditorIconEl([SEND_AI_REQUEST_BUTTON_ICON_CLASS_NAME, 'fas', 'fa-paper-plane-top']),
    );
    return sendRequestButtonEl;
};

const initActionTooltip = (buttonEl: HTMLButtonElement, tooltipText: string) => {
    buttonEl.addEventListener('mouseenter', () => {
        const tooltipEl: HTMLElement = buttonEl.querySelector('.' + REQUEST_ACTION_TOOLTIP_CLASS_NAME);
        tooltipEl?.classList.add(REQUEST_ACTION_TOOLTIP_VISIBLE_CLASS_NAME);
    });
    buttonEl.addEventListener('mouseleave', () => {
        const tooltipEl: HTMLElement = buttonEl.querySelector('.' + REQUEST_ACTION_TOOLTIP_CLASS_NAME);
        tooltipEl?.classList.remove(REQUEST_ACTION_TOOLTIP_VISIBLE_CLASS_NAME);
    });
    const tooltipEl = document.createElement('div');
    tooltipEl.classList.add('tooltip', 'fi-rounded-white-tooltip', REQUEST_ACTION_TOOLTIP_CLASS_NAME);
    tooltipEl.innerText = tooltipText;
    buttonEl.appendChild(tooltipEl);
};

const getAIRequestWrapperElement = (args: IAIWrapperElBuilderArgs) => {
    const aiRequestWrapper: HTMLDivElement = document.createElement('div');
    aiRequestWrapper.classList.add(AI_REQUEST_WRAPPER_CLASS_NAME);

    aiRequestWrapper.appendChild(
        getEditorIconEl([AI_REQUEST_MAIN_ICON_CLASS_NAME, 'BrandedColor', 'far', 'fa-user-robot']),
    );
    aiRequestWrapper.appendChild(getRequestInputEl(args.requestMessage, args.inputPlaceholder, args.sendRequestFn));
    aiRequestWrapper.appendChild(
        getAiRequestActionsWrapperEl({
            sendRequestFn: args.sendRequestFn,
            initialRequestMessage: args.requestMessage,
            sendRequestTooltipText: args.sendRequestTooltipText,
            clearRequestTooltipText: args.clearRequestTooltipText,
        }),
    );

    return aiRequestWrapper;
};

export const setPromptSampleContainerVisibility = (isVisible: boolean) => {
    const container: HTMLElement = document.getElementById(FI_AI_ASSISTANCE_PROMPT_SAMPLES_CONTAINER_ID);
    if (isVisible) {
        container.classList.add(PROMPT_SAMPLE_CONTAINER_VISIBLE_CLASS_NAME);
    } else {
        container.classList.remove(PROMPT_SAMPLE_CONTAINER_VISIBLE_CLASS_NAME);
    }
};

export const setClearRequestButtonVisibility = () => {
    const button: HTMLElement = getExistingClearRequestButtonEl();
    const inputEl: HTMLElement = getExistingRequestInputEl();
    const lineHeight: number = +getComputedStyle(inputEl).lineHeight.slice(0, -2);
    const inputPadding: number = +getComputedStyle(inputEl).paddingTop.slice(0, -2);
    const minInputLines: number = 3;
    const isVisible: boolean = inputEl.offsetHeight >= lineHeight * minInputLines + inputPadding;
    if (isVisible) {
        button.classList.add(CLEAR_AI_REQUEST_VISIBLE_CLASS_NAME);
    } else {
        button.classList.remove(CLEAR_AI_REQUEST_VISIBLE_CLASS_NAME);
    }
};

const getPromptSampleMessageSpanEl = (recentRequestMessage: string): HTMLElement => {
    const promptSampleMessageSpanEl: HTMLSpanElement = document.createElement('span');
    promptSampleMessageSpanEl.classList.add(PROMPT_SAMPLE_LABEL_CLASS_NAME);
    promptSampleMessageSpanEl.innerText = recentRequestMessage;
    return promptSampleMessageSpanEl;
};

const getAIPromptSampleButton = (promptSample: IAIAssistancePrompt): HTMLButtonElement => {
    const promptSampleButtonEl: HTMLButtonElement = document.createElement('button');
    promptSampleButtonEl.classList.add(PROMPT_SAMPLE_BUTTON_CLASS_NAME);
    promptSampleButtonEl.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const inputEl: HTMLElement = getExistingRequestInputEl();
        inputEl.innerText = promptSample.Prompt;
        inputEl.focus();
        handleAiRequestInputChange();
        updateSendRequestButtonElDisableStatus();
    });
    promptSampleButtonEl.appendChild(
        getEditorIconEl([PROMPT_SAMPLE_BUTTON_ICON_CLASS_NAME, 'BrandedColor', 'far', 'fa-stars']),
    );
    promptSampleButtonEl.appendChild(getPromptSampleMessageSpanEl(promptSample.Title));
    return promptSampleButtonEl;
};

const getAIPromptSamplesContainerElement = (promptSamples: IAIAssistancePrompt[], promptSamplesTitle: string) => {
    const aiPromptSamplesContainerEl: HTMLDivElement = document.createElement('div');
    aiPromptSamplesContainerEl.classList.add(PROMPT_SAMPLES_WRAPPER);
    aiPromptSamplesContainerEl.id = FI_AI_ASSISTANCE_PROMPT_SAMPLES_CONTAINER_ID;

    if (promptSamples?.length) {
        aiPromptSamplesContainerEl.appendChild(getAIPromptSamplesTitleElement(promptSamplesTitle));

        promptSamples.forEach((promptSample: IAIAssistancePrompt) => {
            aiPromptSamplesContainerEl.appendChild(getAIPromptSampleButton(promptSample));
        });
    }

    return aiPromptSamplesContainerEl;
};

const getAIPromptSamplesTitleElement = (promptSamplesTitle: string) => {
    const promptSamplesTitleParagraph: HTMLParagraphElement = document.createElement('p');
    promptSamplesTitleParagraph.innerText = promptSamplesTitle;
    promptSamplesTitleParagraph.classList.add(PROMPT_SAMPLES_TITTLE);
    return promptSamplesTitleParagraph;
};
